import * as Api from '@ViewModels';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta } from '../../web/ReactQueryProvider';

export interface IBlogImportSystemJob extends Api.ISystemJob {
	isReverted?: boolean;
	revertedByUserId?: string;
	revertedDateUtc?: Date;
	importedPostCount?: number;
	importSource?: string;
	additionalInfo?: string;
	userReference?: Api.IUserReference;
	rssUrl?: string;
}

interface Params {
	accountId: string;
	showReverted?: boolean;
	meta?: LevStandardQueryMeta;
}

export const getBlogImportHistoryQueryKey = (accountId: string, showReverted?: boolean) => [
	'blogImportHistory',
	accountId,
	showReverted,
];

export const useBlogImportHistoryInvalidation = () => {
	const queryClient = useQueryClient();

	return {
		invalidateBlogImportHistory: (accountId: string) => {
			queryClient.invalidateQueries({ queryKey: ['blogImportHistory', accountId] });
		},
	};
};

export function useBlogImportHistoryQuery({ accountId, showReverted = false, meta }: Params) {
	const userSession = useUserSession();

	return useQuery({
		queryKey: getBlogImportHistoryQueryKey(accountId, showReverted),
		queryFn: async () => {
			const value = await userSession.webServiceHelper.callAsync<IBlogImportSystemJob[]>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext: { account: { id: accountId } },
					urlPath: `blog/imports?showReverted=${showReverted}`,
				}),
				'GET'
			);
			return value;
		},
		enabled: !!accountId,
		meta,
	});
}
